import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import SectionAppBar from "../../common/SectionAppBar";
import {
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../redux/stepSlice";
import SetTaskInfo from "../Tasks/SetTaskInfo";
import { getPages, renderVideo } from "./plugins/utils";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../SectionElements/plugins/utils.js";
import RectangleElement from "../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import { getLastSibling, getSibling } from "../../redux/partSlice.js";
import CanvasDisplay from "../Canvas/components/canvas-core/CanvasDisplay.jsx";
import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import { getAllSectionsForPart } from "../../redux/sectionSlice";
import { renderPreview } from "./plugins/utils";
import { BASE_URL } from "../../config";

const SelectedSectionPage = () => {
  const {
    sectionsName,
    partId,
    revisionId,
    sectionId,
    subheading,
    partNumber,
    revision,
    section,
    stepTitle,
    siblingOrder,
  } = useParams();
  const location = useLocation();
  const { sectionName: initialSectionName, data } = location.state || {};
  const dispatch = useDispatch();
  const [sectionName, setSectionName] = useState(initialSectionName);
  const [sectionDescription, setSectionDescription] = useState("");
  const [sectionData, setSectionData] = useState(null); // Add state for full section object
  // Store the initial counter values
  let attachmentCounter = data.attachmentCounter;
  let canvasCounter = data.canvasCounter;
  let formCounter = data.formCounter;
  let richTextCounter = data.richTextCounter;
  let videoCounter = data.videoCounter;
  let index = data.stepNumber;
  const [stepCount, setStepCount] = useState(0);
  const [stepSibling, setStepSibling] = useState(1);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };
  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  // Add sections selector to get section count
  const { sections } = useSelector((state) => state.section);

  // Add new effect to fetch latest section data
  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const stepCount = await dispatch(
          getLastSibling({
            type: "step",
            // sectionId,
            revisionId,
            partId,
          })
        );
        const stepSibling = await dispatch(
          getSibling({
            type: "step",
            sectionId,
            revisionId,
            partId,
          })
        );
        setStepSibling(parseInt(stepSibling.payload) + 1 || 1);
        setStepCount(parseInt(stepCount.payload) || 0);
        // Fetch section data from the API
        const response = await axios.get(
          `${BASE_URL}section-tree/get-by-id/${sectionId}`
        );
        const data = response.data.results;
        setSectionName(data.section_name);
        setSectionDescription(data.short_description);
        setSectionData(data); // Set the full section data

        // Debug logging to track section data

        SetTaskInfo(data.section_tree_node_id, data.tree_node_id);
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId,
          })
        );
      } catch (error) {
        console.error("Error fetching section data:", error);
      }
    };

    fetchSectionData();
  }, [sectionId]);

  // Add effect to track section data changes
  useEffect(() => {
  }, [sectionName, sectionsName]);

  // Add effect to fetch all sections for the part
  useEffect(() => {
    if (partId && revisionId) {
      dispatch(getAllSectionsForPart({ partId, revisionId }));
    }
  }, [dispatch, partId, revisionId]);

  // Store sections data globally for SectionAppBar to access
  useEffect(() => {
    if (sections && sections.length > 0) {
      // Store sections data globally so SectionAppBar can access it
      window.sectionsData = sections;
    }
  }, [sections]);

  useEffect(() => {
    if (stepsBySection.length > 0) {
      stepsBySection.forEach((step) => {
        const { step_tree_node_id: stepId } = step;

        // Fetch for step-level content
        dispatch(
          getAllRichTextsForStep({ partId, revisionId, sectionId, stepId })
        );
        dispatch(getAllFormsForStep({ partId, revisionId, sectionId, stepId }));
        dispatch(
          getAllVideosForStep({ partId, revisionId, sectionId, stepId })
        );
        dispatch(
          getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId })
        );
        dispatch(
          getAllCanvasesForStep({ partId, revisionId, sectionId, stepId })
        );

        // Fetch substeps
        dispatch(
          getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })
        ).then((action) => {
          const substeps = action.payload?.substeps || [];
          substeps.forEach((substep) => {
            const substepId = substep.sub_step_tree_node_id;
            dispatch(
              getAllRichTextsForSubstep({
                partId,
                revisionId,
                sectionId,
                stepId,
                substepId,
              })
            );
            dispatch(
              getAllFormsForSubstep({
                partId,
                revisionId,
                sectionId,
                stepId,
                substepId,
              })
            );
            dispatch(
              getAllVideosForSubstep({
                partId,
                revisionId,
                sectionId,
                stepId,
                substepId,
              })
            );
            dispatch(
              getAllAttachmentsForSubstep({
                partId,
                revisionId,
                sectionId,
                stepId,
                substepId,
              })
            );
            dispatch(
              getAllCanvasesForSubstep({
                partId,
                revisionId,
                sectionId,
                stepId,
                substepId,
              })
            );
          });
        });
      });
    }
  }, [dispatch, partId, revisionId, sectionId, stepsBySection]);

  return (
    <div>
      <SectionAppBar
        title={`${sectionsName}`}
        isSection={true}
        path={`/add-section`}
        partId={partId}
        revisionId={revisionId}
        sectionId={sectionId}
        subheading={subheading}
        partNumber={partNumber}
        revision={revision}
        section={sectionName || sectionsName} // Use sectionName instead of undefined section
        stepTitle={stepTitle}
        siblingOrder={siblingOrder}
        elementType={"Section"}
        isSelectedPage={true}
        data={{
          ...data,
          section: sectionData || {
            section_name: sectionName || sectionsName,
            short_description: sectionDescription,
          }, // Pass full section object
          sectionName: sectionName || sectionsName, // Add sectionName to data object
          sectionDescription: sectionDescription, // Add section description
          sectionId: sectionId, // Add section ID
          sections: sections || [], // Add sections array
          sectionCount: sections.length, // Add section count
        }}
        sectionsName={sectionsName}
        steps={stepsBySection}
        substepsByStep={substepsByStep}
        sectionName={sectionName}
        forms={forms}
        richTexts={richTexts}
        partType={data.type}
        status={data.status}
        description={data.description}
        fromSiblingOrder={data.fromSiblingOrder}
        fromTreeId={data.fromTreeId}
        stepSibling={stepSibling}
        fromSectionId={sectionId}
        stepCount={data?.stepCounter || stepCount || 0}
        sectionCount={sections.length}
        move_rev_node_info={data?.move_rev_node_info}
        item_code_type={data?.item_code_type}
      />
      <div style={{ padding: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
            paddingBottom: "20px",
            borderBottom: "3px double #e0e0e0",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="checkbox" checked={true} readOnly />
            <h2 style={{ margin: 0 }}> {sectionsName}</h2>
          </div>
        </div>
        {/* <div style={{ marginBottom: "20px" }}>
          <h3
            style={{
              margin: 0,
              color: "#666",
              fontWeight: "normal",
              fontSize: "14px",
            }}
          >
            {sectionName}
          </h3>
        </div> */}
        {sectionName && <p>{sectionName}</p>}
        {sectionDescription && <p>{sectionDescription}</p>}
        <div>
          {stepsBySection[sectionId]?.map((step) => {
            const stepId = step.step_tree_node_id;
            // stepCounter++;

            const substeps = substepsByStep[stepId] || [];

            const substepLetter = String.fromCharCode(65 + substeps.length); // A, B, C...

            // Calculate media counters before navigation
            const currentAttachmentCounter = attachmentCounter;
            const currentVideoCounter = videoCounter;
            const currentFormCounter = formCounter;
            const currentCanvasCounter = canvasCounter;
            const currentRichTextCounter = richTextCounter;

            // Update counters for next step
            //attachmentCounter += myAttachments.length;
            // videoCounter += myVideos.length;
            // formCounter += myForms.length;
            // canvasCounter += myCanvases.length;
            // richTextCounter += myRichTexts.length; // If you're adding one richText per step

            const combinedItems = [
              ...(richTexts[stepId] || []).map((item) => ({
                ...item,
                type: "richText",
              })),
              ...(forms[stepId] || []).map((item) => ({
                ...item,
                type: "form",
              })),
              ...(attachments[stepId] || []).map((item) => ({
                ...item,
                type: "attachment",
              })),
              ...(canvases[stepId] || []).map((item) => ({
                ...item,
                type: "canvas",
              })),
              ...(videos[stepId] || []).map((item) => ({
                ...item,
                type: "video",
              })),
            ]; // Filter out substeps or unwanted items (same as your current filter)
            const filteredItems = combinedItems.filter(
              (item) => !item.substep_id_ref && !item.sub_step_tree_node_id
            );

            filteredItems.sort((a, b) => {
              const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
              const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

              const orderCompare = compareSiblingOrders(orderA, orderB);
              if (orderCompare !== 0) return orderCompare;

              // Fallback: compare created_at
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateB - dateA; // latest comes first
            });
            return (
              <div
                key={stepId}
                className="header-spacing"
                style={{ marginTop: "20px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom: "1px solid #e0e0e0",
                    paddingBottom: "10px",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input type="checkbox" checked={true} disabled={true} />
                    <h3 style={{ margin: 0, fontSize: "23px" }}>
                      Step {index++}
                    </h3>
                  </div>
                  <div
                    style={{
                      color: "#888",
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    {sectionsName} &gt; Step {index - 1}
                  </div>
                </div>
                <p style={{ marginLeft: "5px", whiteSpace: 'pre-wrap' }}>{step.short_description}</p>

                {/* Step Details */}
                <p style={{ marginLeft: "5px" }}>
                  {structuredClone(step.step_details)
                    .sort(
                      (a, b) => new Date(a.created_at) - new Date(b.created_at)
                    )
                    .map((myStep) => {
                      const partNumber = myStep.part_info?.part_number || "";
                      const description = myStep.part_info?.description || "";
                      const qtyText =
                        myStep.qty && myStep.qty !== 0
                          ? `Qty ${myStep.qty}`
                          : "";
                      const partText = partNumber ? ` (${partNumber})` : "";
                      return (
                        <>
                          {myStep.action}
                          {partText} <RenderHtml content={description} />{" "}
                          {qtyText}&nbsp;
                        </>
                      );
                    })}
                </p>

                {/* BOM Table */}
                {step.parts_required && step.parts_required.length > 0 && (
                  <div style={{ marginLeft: "5px", marginTop: "10px" }}>
                    <h4>{step.bom_table_title_info?.category_name || ""}</h4>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>
                            {
                              step.part_number_column_title_ref_info
                                ?.category_name
                            }
                          </th>
                          <th>Description</th>
                          <th>Qty Per</th>
                          <th>Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...step.parts_required]
                          .sort((a, b) => {
                            // 1. Sort by Type orders field (ascending numerically)
                            const orderA = parseInt(
                              a.part_info?.type_info?.orders || 999999,
                              10
                            );
                            const orderB = parseInt(
                              b.part_info?.type_info?.orders || 999999,
                              10
                            );

                            if (orderA !== orderB) return orderA - orderB;

                            // 2. If type orders are equal, sort by part_number (numbers first, then alphabet)
                            const partNumberA = a.part_info?.part_number || "";
                            const partNumberB = b.part_info?.part_number || "";

                            // Helper to split into numeric and alpha
                            const splitPart = (str) => {
                              const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
                              if (!match) return { num: NaN, rest: str };
                              return {
                                num: match[1] ? parseInt(match[1], 10) : NaN,
                                rest: match[2] || str,
                              };
                            };
                            const aSplit = splitPart(partNumberA);
                            const bSplit = splitPart(partNumberB);

                            // Numbers first
                            if (!isNaN(aSplit.num) && isNaN(bSplit.num))
                              return -1;
                            if (isNaN(aSplit.num) && !isNaN(bSplit.num))
                              return 1;
                            if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
                              if (aSplit.num !== bSplit.num)
                                return aSplit.num - bSplit.num;
                              // If numbers equal, compare rest
                              return aSplit.rest.localeCompare(bSplit.rest);
                            }
                            // Both are alpha or both are NaN
                            return partNumberA.localeCompare(partNumberB);
                          })
                          .map((row, idx) => (
                            <tr key={idx}>
                              <td>{row.part_info.type_info.name}</td>
                              <td>{row.part_info.part_number}</td>
                              <td>
                                <RenderHtml
                                  content={row.part_info.description}
                                />
                              </td>
                              <td>{row.qty === 0 ? "" : row.qty}</td>
                              <td>{row.part_info?.unit_info?.unit_name}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Step Content */}

                {filteredItems.map((item, childIndex) => {
                  if (item.substep_id_ref || item.sub_step_tree_node_id)
                    return null;
                  switch (item.type) {
                    case "richText": {
                      const globalRichTextNumber = richTextCounter++; // ✅ use then increment
                      // render richText block here
                      const label = <h2>Rich Text {globalRichTextNumber}</h2>;

                      return (
                        <div
                          key={item.rich_text_tree_node_id}
                          style={{ marginTop: "20px" }}
                        >
                          <label
                            style={{
                              display: "flex",
                              gap: "6px",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              readOnly
                            />
                            {label}
                          </label>

                          <div
                            style={{
                              marginLeft: "5px",
                              marginTop: "-10px",
                            }}
                          >
                            <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                          </div>
                          <div
                            style={{
                              marginLeft: "4px",
                              marginTop: "5px",
                            }}
                          >
                            {item.category_data
                              ? item.category_data?.category_name
                              : item.category_info?.category_name}
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              marginLeft: "4px",
                              marginTop: "5px",
                            }}
                          >
                            <div
                              className="rich-text-content-display"
                              style={{ marginTop: "-6px", clear: "both" }}
                              dangerouslySetInnerHTML={{
                                __html: item.rich_text_content,
                              }}
                            />
                          </div>
                        </div>
                      );
                    }
                    case "video": {
                      const globalVideoNumber = videoCounter++; // ✅ use then increment
                      const label = <h2>Video {globalVideoNumber}</h2>;

                      return (
                        <div key={label} style={{ marginTop: "20px" }}>
                          <label
                            style={{
                              display: "flex",
                              gap: "6px",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              readOnly
                            />
                            {label}
                          </label>

                          {renderVideo(item)}

                          <div>{item.video_url}</div>

                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                        </div>
                      );
                    }

                    case "form": {
                      const globalFormNumber = formCounter++; // ✅ use then increment
                      const label = <h2>Form {globalFormNumber}</h2>;
                      // render form block here
                      return (
                        <div key={label}>
                          <label
                            style={{
                              display: "flex",
                              gap: "6px",
                              cursor: "pointer",
                              marginTop: "20px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              readOnly
                            />

                            {label}
                          </label>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                        </div>
                      );
                    }
                    case "canvas": {
                      const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                      const label = <h3>Canvas {globalCanvasNumber}</h3>;
                      const shapes = item.shapes || [];
                      const pages = getPages(item);

                      return (
                        <div key={label} style={{ marginTop: "40px" }}>
                          <label
                            style={{
                              display: "flex",
                              gap: "6px",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              readOnly
                            />

                            <h2>{label}</h2>
                          </label>

                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>

                          {/* Display all pages vertically */}
                          <CanvasDisplay
                            pages={pages}
                            hasUserContent={hasUserContent}
                            getTextStyle={getTextStyle}
                            stripHtmlPreserveFormatting={
                              stripHtmlPreserveFormatting
                            }
                            estimateKonvaTextHeight={estimateKonvaTextHeight}
                            formatListContent={formatListContent}
                            hasNoteContent={hasNoteContent}
                            hasTaskContent={hasTaskContent}
                            calculateGroupHeight={calculateGroupHeight}
                            calculateTextBlockWidth={calculateTextBlockWidth}
                          />
                        </div>
                      );
                    }
                    case "attachment": {
                      const attachmentList = item.attachments || [];
                      if (attachmentList.length === 0) return null;

                      //  const globalAttachmentNumber =
                      //           attachmentCounter++; // ✅ use then increment
                      const globalAttachmentNumber = attachmentCounter++; // ✅ Increment here per attachment
                      const label = (
                        <h2>Attachment {globalAttachmentNumber}</h2>
                      );

                      return (
                        <div key={item}>
                          <label
                            style={{
                              display: "flex",
                              gap: "6px",
                              cursor: "pointer",
                              marginTop: "20px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={true}
                              disabled={true}
                              readOnly
                            />
                            {label}
                          </label>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                          {attachmentList.map((attachment, i) => (
                            <div key={i} style={{ marginLeft: "20px" }}>
                              {renderPreview(attachment)}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    default:
                      return null;
                  }
                })}

                {/* Substeps */}

                {(substepsByStep[stepId] || []).map((substep, substepIndex) => {
                  const substepId = substep.sub_step_tree_node_id;
                  const combinedItems = [
                    ...(richTexts[substepId] || []).map((item) => ({
                      ...item,
                      type: "richText",
                    })),
                    ...(forms[substepId] || []).map((item) => ({
                      ...item,
                      type: "form",
                    })),
                    ...(attachments[substepId] || []).map((item) => ({
                      ...item,
                      type: "attachment",
                    })),
                    ...(canvases[substepId] || []).map((item) => ({
                      ...item,
                      type: "canvas",
                    })),
                    ...(videos[substepId] || []).map((item) => ({
                      ...item,
                      type: "video",
                    })),
                  ]; // Filter out substeps or unwanted items (same as your current filter)
                  const filteredItems = combinedItems;

                  filteredItems.sort((a, b) => {
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order;
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order;

                    const orderCompare = compareSiblingOrders(orderA, orderB);
                    if (orderCompare !== 0) return orderCompare;

                    // Fallback: compare created_at
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateB - dateA; // latest comes first
                  });

                  // Final substep label like Substep 5A
                  const substepLetter = String.fromCharCode(65 + substepIndex); // A, B, C, ...

                  const globalSubstepLabel = `Substep ${
                    index - 1
                  }${substepLetter}`;

                  return (
                    <div key={substepId} style={{ marginTop: "20px" }}>
                      <div>
                        <label
                          style={{
                            display: "flex",
                            gap: "8px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={true}
                            disabled={true}
                            readOnly
                          />
                          <h2 style={{ margin: 0 }}>{globalSubstepLabel}</h2>
                        </label>

                        <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                      </div>

                      {/* Substep Content */}
                      {filteredItems.map((item, childIndex) => {
                        switch (item.type) {
                          case "richText": {
                            const globalRichTextNumber = richTextCounter++; // ✅ use then increment
                            // render richText block here
                            const label = (
                              <h2>Rich Text {globalRichTextNumber}</h2>
                            );

                            return (
                              <div
                                key={item.rich_text_tree_node_id}
                                style={{ marginTop: "20px" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                  />
                                  {label}
                                </label>

                                <div
                                  style={{
                                    marginLeft: "5px",
                                    marginTop: "-10px",
                                  }}
                                >
                                  <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                </div>
                                <div
                                  style={{
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                  }}
                                >
                                  {item.category_data
                                    ? item.category_data?.category_name
                                    : item.category_info?.category_name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                  }}
                                >
                                  <div
                                    style={{ marginTop: "-6px" }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.rich_text_content,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                          case "video": {
                            const globalVideoNumber = videoCounter++; // ✅ use then increment
                            const label = <h2>Video {globalVideoNumber}</h2>;

                            return (
                              <div key={label} style={{ marginTop: "20px" }}>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                  />
                                  {label}
                                </label>

                                {renderVideo(item)}

                                <div>{item.video_url}</div>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                              </div>
                            );
                          }

                          case "form": {
                            const globalFormNumber = formCounter++; // ✅ use then increment
                            const label = <h2>Form {globalFormNumber}</h2>;
                            // render form block here
                            return (
                              <div key={label}>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                  />

                                  {label}
                                </label>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                              </div>
                            );
                          }
                          case "canvas": {
                            const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                            const label = <h3>Canvas {globalCanvasNumber}</h3>;
                            const shapes = item.shapes || [];
                            const pages = getPages(item);

                            return (
                              <div key={label} style={{ marginTop: "40px" }}>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                  />

                                  <h2>{label}</h2>
                                </label>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>

                                {/* Display all pages vertically */}
                                <CanvasDisplay pages={pages} />
                              </div>
                            );
                          }
                          case "attachment": {
                            const attachmentList = item.attachments || [];
                            if (attachmentList.length === 0) return null;

                            //  const globalAttachmentNumber =
                            //           attachmentCounter++; // ✅ use then increment
                            const globalAttachmentNumber = attachmentCounter++; // ✅ Increment here per attachment
                            const label = (
                              <h2>Attachment {globalAttachmentNumber}</h2>
                            );

                            return (
                              <div key={item}>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                  />
                                  {label}
                                </label>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{item.short_description}</div>
                                {attachmentList.map((attachment, i) => (
                                  <div key={i} style={{ marginLeft: "20px" }}>
                                    {renderPreview(attachment)}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          default:
                            return null;
                        }
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SelectedSectionPage;
